// Ready to Pay -> QuickBooks, on screen.
//
// Greg + Mercedes, 2026-10-14: tick the approved bills, send them to QuickBooks itemized,
// and anything that will not go straight in lands in a Needs attention list with the
// reason. Nothing here changes a number to make a bill fit - a person fixes the lines on
// the bill (QboLinesCard) and the push copies exactly what is saved.
//
// Three pieces:
//   QboNeedsAttention - the list on Ready to Pay, with a fix offered per reason
//   QboSendDialog     - review every bill, line and account, then send them one at a time
//   QboLinesCard      - on a bill: the lines that will go, with accounts; read, fix, save
import React, { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, Check, X, RefreshCw, Link2, Plus, Trash2, CheckCircle2, Send } from 'lucide-react';
import { apQboPush } from '../services/api';

const money = (v) => (v == null || v === '' || !isFinite(Number(v)))
  ? '—'
  : '$' + Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const cents = (v) => Math.round(Number(v || 0) * 100) / 100;

// The denominator in "@ $X/<unit>" written the way Mercedes writes a memo in QuickBooks.
// Weights and volumes stay as the measurement ("$0.48/lb.", "$0.032/oz."). The handful of
// countable containers she writes "@ $X/ea." for (bushels, boxes, cases, pots, dozens,
// pieces) collapse to "ea." so a crabber line reads "28 bu. #1's @ $130.00/ea.". Anything
// else - coolers, skids, pallets, bags, rolls, drums, gallons, feet - keeps its own word
// in the singular, so a LiFoam line reads "76 cooler @ $36.25/cooler" and not "/ea.".
const perLabel = (unit) => {
  const u = String(unit == null ? '' : unit).trim();
  if (!u) return 'ea.';
  if (/^(bu\.?|bushels?|boxes?|bx|cases?|cs\.?|pots?|pcs?\.?|pieces?|dozens?|dz\.?|eggs?|units?|ea\.?)$/i.test(u)) return 'ea.';
  if (/^lbs?\.?$/i.test(u)) return 'lb.';
  if (/^ozs?\.?$/i.test(u)) return 'oz.';
  // Strip a trailing plural "s" so "4 skids @ $688.70/skid" reads naturally; keep a word
  // like "lbs" alone (handled above) and words that end in "ss" ("glass").
  return u.replace(/([^s])s$/i, '$1');
};

const card = {
  background: 'white', border: '1px solid var(--gray-200, #e5e7eb)',
  borderRadius: 8, marginBottom: 16,
};
const cardHead = {
  padding: '12px 16px', borderBottom: '1px solid var(--gray-100, #f3f4f6)',
  fontWeight: 600, fontSize: 13, color: 'var(--navy, #00395C)',
  display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'space-between',
};
const th = {
  padding: '7px 10px', textAlign: 'left', fontSize: 10, fontWeight: 700,
  color: 'var(--gray-500, #6b7280)', textTransform: 'uppercase', letterSpacing: '0.4px', whiteSpace: 'nowrap',
};
const td = { padding: '8px 10px', verticalAlign: 'top' };
const input = {
  width: '100%', padding: '6px 8px', border: '1px solid var(--gray-300, #d1d5db)',
  borderRadius: 6, fontSize: 13, fontFamily: 'inherit',
};

const CONF = {
  high: { bg: '#e8f5ec', fg: '#1c7c43', label: 'sure' },
  medium: { bg: '#eef4fa', fg: 'var(--navy, #00395C)', label: 'likely' },
  low: { bg: '#fff3e6', fg: '#9a4a1c', label: 'check' },
  none: { bg: '#fdeaea', fg: '#b42318', label: 'pick one' },
};
function ConfChip({ level, title }) {
  const m = CONF[level] || CONF.none;
  return (
    <span title={title || ''} style={{
      background: m.bg, color: m.fg, fontSize: 10, fontWeight: 700, padding: '2px 7px',
      borderRadius: 999, textTransform: 'uppercase', letterSpacing: '0.3px', whiteSpace: 'nowrap',
    }}>{m.label}</span>
  );
}

// A QuickBooks bill, opened in QuickBooks Online. It opens in whichever company the
// browser is signed in to, so the title says which company it belongs to.
// Which company's books a bill goes into, said in colour so a wrong one stands out.
// Mercedes, Oct 21, 2026: "I'd like the company label on each bill bold and color-coded
// (Crab Place orange, Crab & Cruise green), so I can tell at a glance which company a bill
// is posting to. That would have caught the J&T bill" - a $6,220 crabber bill that was
// headed for Crab & Cruise's QuickBooks.
export function CompanyTag({ company }) {
  if (!company) return null;
  const cnc = /cruise/i.test(company);
  return (
    <span style={{
      fontWeight: 800, fontSize: 12, whiteSpace: 'nowrap',
      color: cnc ? '#1c7c43' : 'var(--orange, #DC582A)',
    }}>{company}</span>
  );
}

export const qboBillUrl = (billId) => 'https://qbo.intuit.com/app/bill?txnId=' + encodeURIComponent(billId);

// ⚠️ "CHECKING" HAS TO SAY WHAT IT IS CHECKING AND HAS TO END. Mercedes, Oct 14, 2026: "I
// don't know what this state means... It should resolve to Ready / Needs attention /
// Entered, not sit there indefinitely." It is the per-bill check against QuickBooks; the
// page now runs it a few bills at a time, so a slow one cannot hold the rest, and a check
// that fails says "Could not check" with the reason instead of staying grey forever.
const CHECKING_TITLE = 'Checking this bill against QuickBooks: is the vendor there, does every line have an account, '
  + 'do the lines add up to what was approved, and is it already in QuickBooks. A few seconds per bill.';

// The chip in Ready to Pay's QuickBooks column for a bill not yet entered.
export function QboStatusChip({ check }) {
  if (!check) return <span title={CHECKING_TITLE} style={{ color: 'var(--gray-500, #6b7280)', fontSize: 12.5, cursor: 'help' }}>Checking…</span>;
  const map = {
    ready: { bg: '#eef4fa', fg: 'var(--navy, #00395C)', border: '#c9dcee', text: 'Ready to send' },
    attention: { bg: '#fff3e6', fg: '#9a4a1c', border: '#f5cfa8', text: 'Needs attention' },
    in_qb: { bg: '#fff3e6', fg: '#9a4a1c', border: '#f5cfa8', text: 'Already in QB?' },
    error: { bg: '#fdeaea', fg: '#b42318', border: '#f3c1bc', text: 'Could not check' },
  };
  const m = map[check.status];
  if (!m) return <span style={{ color: 'var(--gray-400, #9ca3af)', fontSize: 12.5 }}>not entered</span>;
  return (
    <span title={(check.issues || []).map((i) => i.text).join('\n')} style={{
      background: m.bg, color: m.fg, border: '1px solid ' + m.border, fontSize: 10, fontWeight: 800,
      padding: '2px 7px', borderRadius: 999, textTransform: 'uppercase', letterSpacing: '0.4px', whiteSpace: 'nowrap',
    }}>{m.text}</span>
  );
}

// ─── vendor picker: once picked, remembered for this vendor ─────────────────────
function VendorPicker({ check, onDone }) {
  const [all, setAll] = useState(null);
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const pick = async (id) => {
    setBusy(true); setErr('');
    try { const r = await apQboPush('link_vendor', { documentId: check.documentId, qboVendorId: id }); onDone(r.check); }
    catch (e) { setErr(e.message); }
    setBusy(false);
  };
  const loadAll = async () => {
    setBusy(true);
    try { const r = await apQboPush('vendors', { documentId: check.documentId }); setAll(r.vendors || []); }
    catch (e) { setErr(e.message); }
    setBusy(false);
  };
  const list = all
    ? all.filter((v) => !q || v.name.toLowerCase().includes(q.toLowerCase())).slice(0, 30)
    : (check.vendorCandidates || []);
  return (
    <div style={{ marginTop: 6 }}>
      <div style={{ fontSize: 12, color: 'var(--gray-600, #4b5563)', marginBottom: 4 }}>
        Which vendor is "{check.vendorName}" in {check.company}?
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
        {list.map((v) => (
          <button key={v.id} className="btn btn-outline" disabled={busy}
            style={{ fontSize: 12, padding: '3px 9px' }} onClick={() => pick(v.id)}>{v.name}</button>
        ))}
        {!all && (
          <button className="btn btn-ghost" disabled={busy} style={{ fontSize: 12 }} onClick={loadAll}>
            {busy ? 'Loading…' : 'Search all vendors'}
          </button>
        )}
        {all && (
          <input style={{ ...input, width: 220 }} placeholder="Type to search vendors"
            value={q} onChange={(e) => setQ(e.target.value)} autoFocus />
        )}
      </div>
      {err && <div style={{ color: '#b42318', fontSize: 12, marginTop: 4 }}>{err}</div>}
    </div>
  );
}

// ─── "It is already in QuickBooks": find it, confirm it, link it ───────────────
// Mercedes, Oct 14, 2026: "Mark-entered should link to the existing QBO bill: search
// open/recent QBO bills for that vendor by amount, invoice #, and date, and let me
// confirm. It must not create a duplicate." Linking writes to CPx only; the server reads
// the picked bill live from QuickBooks and refuses it unless the total is the approved
// amount to the cent.
const fmtDay = (ymd) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(ymd || ''));
  if (!m) return '';
  return ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][Number(m[2]) - 1]
    + ' ' + Number(m[3]) + ', ' + m[1];
};
function MatchTag({ ok, children }) {
  return (
    <span style={{
      fontSize: 10.5, fontWeight: 700, padding: '1px 6px', borderRadius: 999, whiteSpace: 'nowrap',
      background: ok ? '#e8f5ec' : 'var(--gray-100, #f3f4f6)', color: ok ? '#1c7c43' : 'var(--gray-500, #6b7280)',
    }}>{ok ? '✓ ' : ''}{children}</span>
  );
}
function ExistingPicker({ check, onLinked, onClose }) {
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  useEffect(() => {
    let live = true;
    apQboPush('find_existing', { documentId: check.documentId })
      .then((r) => { if (live) setRes(r); })
      .catch((e) => { if (live) setErr(e.message); });
    return () => { live = false; };
  }, [check.documentId]);
  const link = async (billId) => {
    setBusy(billId); setErr('');
    try {
      const r = await apQboPush('link_existing', { documentId: check.documentId, billId });
      onLinked(r.result);
    } catch (e) { setErr(e.message); }
    setBusy('');
  };
  const cands = (res && res.candidates) || [];
  return (
    <div style={{ marginTop: 8, padding: '8px 10px', background: 'var(--gray-50, #f9fafb)', borderRadius: 6, border: '1px solid var(--gray-200, #e5e7eb)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12.5, marginBottom: 6 }}>
        <strong style={{ color: 'var(--navy, #00395C)' }}>Which QuickBooks bill is it?</strong>
        <button className="btn btn-ghost" style={{ padding: 2 }} onClick={onClose}><X size={13} /></button>
      </div>
      {!res && !err && <div style={{ fontSize: 12.5, color: 'var(--gray-500, #6b7280)' }}>Looking in QuickBooks…</div>}
      {res && !cands.length && (
        <div style={{ fontSize: 12.5, color: 'var(--gray-600, #4b5563)' }}>
          No bill in QuickBooks matches this vendor, invoice number or amount. QuickBooks is copied here every hour,
          so a bill entered in the last hour may not show yet.
        </div>
      )}
      {cands.map((b) => (
        <div key={b.billId} style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', padding: '5px 0', borderTop: '1px solid var(--gray-100, #f3f4f6)', fontSize: 12.5 }}>
          <span style={{ minWidth: 220 }}>
            <strong>{b.vendor}</strong> · #{b.docNumber || 'no number'} · {money(b.total)} · {fmtDay(b.txnDate)}
          </span>
          <span style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            <MatchTag ok={b.matches.number}>number</MatchTag>
            <MatchTag ok={b.matches.vendor}>vendor</MatchTag>
            <MatchTag ok={b.matches.amount}>amount</MatchTag>
            <MatchTag ok={b.matches.date}>date</MatchTag>
          </span>
          <a href={qboBillUrl(b.billId)} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12 }}>Open in QuickBooks</a>
          <span style={{ marginLeft: 'auto' }}>
            {b.linkedTo
              ? <span style={{ fontSize: 12, color: 'var(--gray-500, #6b7280)' }}>already linked to CPx bill #{b.linkedTo}</span>
              : (
                <button className="btn btn-outline" disabled={!!busy || !b.matches.amount} style={{ fontSize: 12, padding: '3px 10px' }}
                  title={b.matches.amount ? 'Link this bill to it in CPx. Nothing is created in QuickBooks.' : 'The amount does not match what was approved, so it cannot be linked.'}
                  onClick={() => link(b.billId)}>
                  <Link2 size={12} /> {busy === b.billId ? 'Linking…' : 'This is the one'}
                </button>
              )}
          </span>
        </div>
      ))}
      {err && <div style={{ color: '#b42318', fontSize: 12, marginTop: 4 }}>{err}</div>}
    </div>
  );
}

// ─── lines with no account (or a guessed one): pick per line, right here ────────
// Mercedes, Oct 14, 2026 (Tim Howlett): "An inline QBO account picker on any line with no
// account, usable at the ready-to-pay/approval step, so I can assign accounts per line."
function AccountFixer({ check, onSaved }) {
  const [accounts, setAccounts] = useState(null);
  const [lines, setLines] = useState(() => (check.lines || []).map((l) => ({ ...l, touched: false })));
  const [remember, setRemember] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  useEffect(() => {
    let live = true;
    apQboPush('accounts', { documentId: check.documentId })
      .then((r) => { if (live) setAccounts(r.accounts || []); })
      .catch((e) => { if (live) setErr(e.message); });
    return () => { live = false; };
  }, [check.documentId]);
  const needs = (l) => !l.account_id || l.guess === 'last_bill' || l.confidence === 'none';
  const one = new Set(lines.map((l) => l.account_id).filter(Boolean));
  const sameForAll = lines.length > 0 && one.size === 1 && lines.every((l) => l.account_id);
  const save = async () => {
    setBusy(true); setErr('');
    try {
      // A guess she leaves as it is counts as confirmed: saving it makes it hers.
      const r = await apQboPush('save_lines', {
        documentId: check.documentId,
        lines: lines.map((l) => ({ ...l, touched: l.touched || l.guess === 'last_bill' })),
      });
      let next = r.check;
      if (remember && sameForAll && check.vendor) {
        const d = await apQboPush('vendor_default', { documentId: check.documentId, set: true, accountId: [...one][0] });
        next = d.check || next;
      }
      onSaved(next);
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };
  return (
    <div style={{ marginTop: 8, padding: '8px 10px', background: 'var(--gray-50, #f9fafb)', borderRadius: 6, border: '1px solid var(--gray-200, #e5e7eb)' }}>
      {check.whatFor && (
        <div style={{ fontSize: 12, color: 'var(--gray-600, #4b5563)', marginBottom: 6 }}>
          What it is for, as entered: <strong>{check.whatFor}</strong>
        </div>
      )}
      <table style={{ width: '100%', fontSize: 12.5, borderCollapse: 'collapse' }}>
        <tbody>
          {lines.map((l, i) => (
            <tr key={i} style={{ borderTop: '1px solid var(--gray-100, #f3f4f6)' }}>
              <td style={{ ...td, padding: '4px 6px' }}>{l.description}</td>
              <td style={{ ...td, padding: '4px 6px', minWidth: 280 }}>
                <select style={{ ...input, borderColor: needs(l) && !l.touched ? '#f5cfa8' : undefined }}
                  value={l.account_id || ''} disabled={!accounts}
                  onChange={(e) => {
                    const a = (accounts || []).find((x) => x.id === e.target.value);
                    setLines((ls) => ls.map((x, n) => (n === i ? {
                      ...x, account_id: a ? a.id : '', account_name: a ? a.name : null,
                      confidence: 'high', account_reason: 'chosen by a person', guess: null, touched: true,
                    } : x)));
                  }}>
                  <option value="">{accounts ? 'Pick an account…' : 'Loading accounts…'}</option>
                  {l.account_id && !(accounts || []).some((a) => a.id === l.account_id) && (
                    <option value={l.account_id}>{l.account_name}</option>
                  )}
                  {(accounts || []).map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
                {l.guess === 'last_bill' && !l.touched && (
                  <div style={{ fontSize: 11, color: '#9a4a1c' }}>From this vendor's last bill. Confirm or change.</div>
                )}
              </td>
              <td style={{ ...td, padding: '4px 6px', textAlign: 'right', whiteSpace: 'nowrap' }}>{money(l.amount)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 8, flexWrap: 'wrap' }}>
        {check.vendor && sameForAll && (
          <label style={{ fontSize: 12, display: 'flex', gap: 6, alignItems: 'center' }}>
            <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} />
            Use this account for every {check.vendor.name} line from now on
          </label>
        )}
        <button className="btn btn-primary" disabled={busy || !accounts || lines.some((l) => !l.account_id)}
          style={{ fontSize: 12, padding: '4px 12px', marginLeft: 'auto' }} onClick={save}>
          {busy ? 'Saving…' : 'Save accounts'}
        </button>
      </div>
      {err && <div style={{ color: '#b42318', fontSize: 12, marginTop: 4 }}>{err}</div>}
    </div>
  );
}

// Which QuickBooks account a payment method posts to. Picked once per method and
// remembered for every already-paid purchase after it (Mercedes, Oct 21, 2026). Only
// card and bank accounts are offered: those are the only things a charge can sit on.
function PaidAccountPicker({ check, onDone }) {
  const [accounts, setAccounts] = useState(null);
  const [pick, setPick] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  useEffect(() => {
    let live = true;
    apQboPush('accounts', { documentId: check.documentId })
      .then((r) => { if (live) setAccounts((r.accounts || []).filter((a) => /^(credit card|bank)$/i.test(String(a.type || '')))); })
      .catch((e) => { if (live) setErr(e.message); });
    return () => { live = false; };
  }, [check.documentId]);
  const save = async () => {
    setBusy(true); setErr('');
    try {
      const r = await apQboPush('paid_account', { documentId: check.documentId, accountId: pick });
      onDone(r.check);
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };
  const label = check.paidWith ? check.paidWith.label : 'this method';
  return (
    <div style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', fontSize: 12.5 }}>
      <span>{label} posts to</span>
      <select value={pick} onChange={(e) => setPick(e.target.value)} disabled={!accounts}
        style={{ fontSize: 12.5, padding: '4px 6px', border: '1px solid var(--gray-300, #d1d5db)', borderRadius: 4, maxWidth: 320 }}>
        <option value="">{accounts ? 'Pick the card or bank account…' : 'Loading…'}</option>
        {(accounts || []).map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
      </select>
      <button className="btn btn-primary" style={{ fontSize: 12, padding: '3px 10px' }} disabled={!pick || busy} onClick={save}>
        {busy ? 'Saving…' : 'Use it from now on'}
      </button>
      {err && <span style={{ color: '#b42318' }}>{err}</span>}
    </div>
  );
}

// ─── Needs attention ───────────────────────────────────────────────────────────
export function QboNeedsAttention({ checks, rows, onOpen, onCheckChanged, onEntered, canEnter }) {
  const [finding, setFinding] = useState(null);   // documentId whose QuickBooks bill is being looked for
  const [fixing, setFixing] = useState(null);     // documentId whose accounts are open
  const [busyId, setBusyId] = useState(null);
  const [errs, setErrs] = useState({});
  const [readingAll, setReadingAll] = useState(false);
  const byId = new Map((rows || []).map((r) => [r.id, r]));
  const list = (checks || []).filter((c) =>
    c.status === 'attention' || c.status === 'in_qb' || c.lastError || c.attachError);
  if (!list.length) return null;

  const run = async (c, fn) => {
    setBusyId(c.documentId); setErrs((e) => ({ ...e, [c.documentId]: '' }));
    try { await fn(); } catch (e) { setErrs((x) => ({ ...x, [c.documentId]: e.message })); }
    setBusyId(null);
  };
  const readLines = (c) => run(c, async () => {
    const r = await apQboPush('read_lines', { documentId: c.documentId });
    if (r.read && !r.read.ok) throw new Error('Could not read the lines: ' + r.read.reason);
    onCheckChanged(r.check);
  });
  // Linked by the QuickBooks bill itself, so it no longer needs the vendor picked first.
  const link = (c) => run(c, async () => {
    const r = await apQboPush('link_existing', { documentId: c.documentId, billId: c.duplicate && c.duplicate.billId });
    onCheckChanged(r.result); onEntered();
  });
  const notDup = (c) => run(c, async () => {
    const r = await apQboPush('not_duplicate', { documentId: c.documentId, billId: c.duplicate.billId });
    onCheckChanged(r.check);
  });
  // Bills from before this existed have never had their lines read. One click reads them
  // all, one at a time, so a slow or failed one never holds up the rest.
  const unread = list.filter((c) => c.linesUnread);
  const readAll = async () => {
    setReadingAll(true);
    for (const c of unread) {
      setBusyId(c.documentId);
      try {
        const r = await apQboPush('read_lines', { documentId: c.documentId });
        if (r.read && !r.read.ok) setErrs((x) => ({ ...x, [c.documentId]: 'Could not read the lines: ' + r.read.reason }));
        onCheckChanged(r.check);
      } catch (e) { setErrs((x) => ({ ...x, [c.documentId]: e.message })); }
    }
    setBusyId(null);
    setReadingAll(false);
  };

  return (
    <div style={{ ...card, borderColor: '#f5cfa8' }}>
      <div style={{ ...cardHead, background: '#fff8f1', color: '#9a4a1c' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          <AlertTriangle size={14} /> Needs attention before QuickBooks ({list.length})
        </span>
        <span style={{ display: 'flex', gap: 10, alignItems: 'center', fontWeight: 400, fontSize: 12 }}>
          Nothing on this list is changed to make it fit. Fix it, and it goes.
          {unread.length > 0 && (
            <button className="btn btn-outline" disabled={readingAll} style={{ fontSize: 12, padding: '3px 10px' }} onClick={readAll}>
              {readingAll ? 'Reading…' : `Read the lines on ${unread.length} invoice${unread.length === 1 ? '' : 's'}`}
            </button>
          )}
        </span>
      </div>
      {list.map((c) => {
        const row = byId.get(c.documentId) || {};
        const codes = new Set((c.issues || []).map((i) => i.code));
        const busy = busyId === c.documentId;
        return (
          <div key={c.documentId} style={{ padding: '10px 16px', borderTop: '1px solid var(--gray-100, #f3f4f6)', fontSize: 13 }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'baseline', flexWrap: 'wrap' }}>
              <strong style={{ color: 'var(--navy, #00395C)', cursor: 'pointer' }} onClick={() => onOpen(c.documentId)}>
                {c.vendorName || row.vendor_name}
              </strong>
              <span style={{ color: 'var(--gray-500, #6b7280)' }}>#{c.invoiceNumber || row.invoice_number || '—'}</span>
              <span>{money(c.approved)}</span>
              <CompanyTag company={c.company} />
            </div>
            <ul style={{ margin: '5px 0 0', paddingLeft: 18, color: '#7a3b14' }}>
              {(c.issues || []).map((i, n) => <li key={n}>{i.text}</li>)}
              {c.lastError && <li>Last try failed: {c.lastError}</li>}
              {c.attachError && <li>The bill is in QuickBooks, but the scan did not attach: {c.attachError}. Attach it there by hand.</li>}
            </ul>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
              <button className="btn btn-outline" style={{ fontSize: 12, padding: '3px 10px' }} onClick={() => onOpen(c.documentId)}>
                Open the bill
              </button>
              {codes.has('lines') && (
                <button className="btn btn-outline" disabled={busy} style={{ fontSize: 12, padding: '3px 10px' }} onClick={() => readLines(c)}>
                  {busy ? 'Reading…' : 'Read the lines off the invoice'}
                </button>
              )}
              {c.status === 'in_qb' && canEnter && c.duplicate && cents(c.duplicate.total) === cents(c.approved) && (
                <button className="btn btn-outline" disabled={busy} style={{ fontSize: 12, padding: '3px 10px' }} onClick={() => link(c)}>
                  <Link2 size={12} /> {busy ? 'Linking…' : 'It is the same bill - mark it entered'}
                </button>
              )}
              {c.status === 'in_qb' && canEnter && c.duplicate && c.duplicate.maybe && (
                <button className="btn btn-outline" disabled={busy} style={{ fontSize: 12, padding: '3px 10px' }} onClick={() => notDup(c)}
                  title="That QuickBooks bill is a different one. This bill can then be sent.">
                  It is a different bill
                </button>
              )}
              {c.duplicate && (
                <a href={qboBillUrl(c.duplicate.billId)} target="_blank" rel="noopener noreferrer"
                  style={{ fontSize: 12, alignSelf: 'center' }}>Open that bill in QuickBooks</a>
              )}
              {canEnter && (
                <button className="btn btn-ghost" style={{ fontSize: 12, padding: '3px 10px' }}
                  onClick={() => setFinding(finding === c.documentId ? null : c.documentId)}>
                  Already in QuickBooks? Find it
                </button>
              )}
              {canEnter && codes.has('account') && (c.lines || []).length > 0 && (
                <button className="btn btn-outline" style={{ fontSize: 12, padding: '3px 10px' }}
                  onClick={() => setFixing(fixing === c.documentId ? null : c.documentId)}>
                  Pick the accounts here
                </button>
              )}
            </div>
            {codes.has('vendor') && <VendorPicker check={c} onDone={onCheckChanged} />}
            {canEnter && codes.has('paid_account') && <PaidAccountPicker check={c} onDone={onCheckChanged} />}
            {finding === c.documentId && (
              <ExistingPicker check={c} onClose={() => setFinding(null)}
                onLinked={(r) => { setFinding(null); onCheckChanged(r); onEntered(); }} />
            )}
            {fixing === c.documentId && (
              <AccountFixer check={c} onSaved={(r) => { setFixing(null); onCheckChanged(r); }} />
            )}
            {errs[c.documentId] && <div style={{ color: '#b42318', fontSize: 12, marginTop: 4 }}>{errs[c.documentId]}</div>}
          </div>
        );
      })}
    </div>
  );
}

// ─── review + send ─────────────────────────────────────────────────────────────
export function QboSendDialog({ checks, onClose, onDone }) {
  const [state, setState] = useState({}); // id -> 'sending' | 'done' | 'failed'
  const [results, setResults] = useState({});
  const [running, setRunning] = useState(false);
  const total = cents(checks.reduce((a, c) => a + Number(c.approved || 0), 0));
  const finished = Object.keys(state).length === checks.length && !running;

  const send = async () => {
    setRunning(true);
    // One at a time, on purpose: each bill is its own decision in QuickBooks, and a
    // failure on one must leave the others exactly as they were.
    for (const c of checks) {
      setState((s) => ({ ...s, [c.documentId]: 'sending' }));
      try {
        const r = await apQboPush('push', { documentId: c.documentId });
        const ok = r.result && r.result.status === 'pushed';
        setResults((x) => ({ ...x, [c.documentId]: r.result }));
        setState((s) => ({ ...s, [c.documentId]: ok ? 'done' : 'failed' }));
      } catch (e) {
        setResults((x) => ({ ...x, [c.documentId]: { issues: [{ text: e.message }] } }));
        setState((s) => ({ ...s, [c.documentId]: 'failed' }));
      }
    }
    setRunning(false);
  };

  return (
    <div onClick={() => !running && onClose()} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,20,40,0.45)', zIndex: 1000,
      display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 16px', overflowY: 'auto',
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{ ...card, width: '100%', maxWidth: 980, marginBottom: 40 }}>
        <div style={cardHead}>
          <span>Send {checks.length} bill{checks.length === 1 ? '' : 's'} to QuickBooks · {money(total)}</span>
          <button className="btn btn-ghost" disabled={running} onClick={onClose}><X size={14} /></button>
        </div>
        <div style={{ padding: '10px 16px', fontSize: 12.5, color: 'var(--gray-600, #4b5563)' }}>
          Each one goes in as a bill with these lines and accounts, dated the invoice date, due on the due date,
          with the scan attached, and is marked entered here. It is not paid - you still pay it in QuickBooks.
        </div>
        {checks.map((c) => {
          const st = state[c.documentId];
          const res = results[c.documentId];
          return (
            <div key={c.documentId} style={{ borderTop: '1px solid var(--gray-100, #f3f4f6)', padding: '10px 16px' }}>
              <div style={{ display: 'flex', gap: 12, alignItems: 'baseline', flexWrap: 'wrap', fontSize: 13 }}>
                <strong style={{ color: 'var(--navy, #00395C)' }}>{c.vendor ? c.vendor.name : c.vendorName}</strong>
                <span style={{ color: 'var(--gray-500, #6b7280)' }}>#{c.invoiceNumber}</span>
                <CompanyTag company={c.company} />
                <strong style={{ marginLeft: 'auto' }}>{money(c.approved)}</strong>
                <span style={{ minWidth: 90, textAlign: 'right', fontSize: 12 }}>
                  {st === 'sending' && 'Sending…'}
                  {st === 'done' && <span style={{ color: '#1c7c43', fontWeight: 700 }}><Check size={12} /> In QuickBooks</span>}
                  {st === 'failed' && <span style={{ color: '#b42318', fontWeight: 700 }}>Not sent</span>}
                </span>
              </div>
              {c.paidWith && (
                <div style={{ fontSize: 12, color: '#1c7c43', marginTop: 3 }}>
                  Already paid with {c.paidWith.label}{c.paidWith.checkNumber && c.paidWith.kind === 'check' ? ' #' + c.paidWith.checkNumber : ''}.
                  {' '}Goes in as a {c.paidWith.kind === 'card' ? 'charge on' : 'payment from'} {c.paidWith.accountName}, and closes as paid here.
                </div>
              )}
              <table style={{ width: '100%', fontSize: 12.5, borderCollapse: 'collapse', marginTop: 6 }}>
                <tbody>
                  {(c.lines || []).map((l, i) => (
                    <tr key={i} style={{ borderTop: '1px solid var(--gray-100, #f3f4f6)' }}>
                      <td style={{ ...td, padding: '4px 8px' }}>{l.description}</td>
                      <td style={{ ...td, padding: '4px 8px', color: 'var(--gray-600, #4b5563)' }}>{l.account_name}</td>
                      <td style={{ ...td, padding: '4px 8px' }}><ConfChip level={l.confidence} title={l.account_reason} /></td>
                      <td style={{ ...td, padding: '4px 8px', textAlign: 'right', whiteSpace: 'nowrap' }}>{money(l.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {st === 'done' && res && res.attachError && (
                <div style={{ color: '#9a4a1c', fontSize: 12, marginTop: 4 }}>The bill went in, but the scan did not attach: {res.attachError}</div>
              )}
              {st === 'failed' && res && (
                <div style={{ color: '#b42318', fontSize: 12, marginTop: 4 }}>
                  {(res.issues || []).map((i) => i.text).join(' ')} It is on Needs attention.
                </div>
              )}
            </div>
          );
        })}
        <div style={{ padding: '12px 16px', display: 'flex', gap: 10, justifyContent: 'flex-end', borderTop: '1px solid var(--gray-100, #f3f4f6)' }}>
          {!finished && (
            <>
              <button className="btn btn-outline" disabled={running} onClick={onClose}>Cancel</button>
              <button className="btn btn-primary" disabled={running} onClick={send}>
                <Send size={13} /> {running ? 'Sending…' : `Send ${checks.length} to QuickBooks`}
              </button>
            </>
          )}
          {finished && <button className="btn btn-primary" onClick={onDone}>Done</button>}
        </div>
      </div>
    </div>
  );
}

// ─── on a bill: the lines that will go to QuickBooks ────────────────────────────
export function QboLinesCard({ doc, onChanged }) {
  const [check, setCheck] = useState(null);
  const [accounts, setAccounts] = useState(null);
  const [edit, setEdit] = useState(null); // working copy of lines while editing
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const id = doc && doc.id;
  const approved = cents(doc && (doc.approved_amount != null ? doc.approved_amount : doc.invoice_amount));

  const refresh = useCallback(async () => {
    try { const r = await apQboPush('check', { documentIds: [id] }); setCheck((r.results || [])[0] || null); }
    catch (e) { setErr(e.message); }
  }, [id]);
  useEffect(() => { if (id) refresh(); }, [id, refresh]);

  // A bill whose lines were never read gets read once, the first time anyone opens it -
  // so by the time it reaches the final approver the lines and accounts are on it.
  // Only "never tried": a read that failed waits for a person to ask again.
  const autoTried = React.useRef(false);
  useEffect(() => {
    if (!check || !check.linesUnread || autoTried.current || check.linesSource === 'trip') return;
    autoTried.current = true;
    read(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [check]);

  const loadAccounts = async () => {
    if (accounts) return accounts;
    const r = await apQboPush('accounts', { documentId: id });
    setAccounts(r.accounts || []);
    return r.accounts || [];
  };
  const read = async (force) => {
    if (force && !window.confirm('Read the lines off the invoice again? This replaces the lines on this bill.')) return;
    setBusy('read'); setErr('');
    try {
      const r = await apQboPush('read_lines', { documentId: id, force: !!force });
      if (r.read && !r.read.ok) setErr('Could not read the lines: ' + r.read.reason);
      setCheck(r.check); onChanged && onChanged();
    } catch (e) { setErr(e.message); }
    setBusy('');
  };
  const startEdit = async () => {
    setErr('');
    try { await loadAccounts(); } catch (e) { setErr(e.message); return; }
    const src = (check && check.lines && check.lines.length) ? check.lines : [{ description: '', amount: approved, account_id: '' }];
    setEdit(src.map((l) => ({ ...l, amount: l.amount == null ? '' : String(l.amount), touched: false })));
  };
  const save = async () => {
    setBusy('save'); setErr('');
    try {
      const r = await apQboPush('save_lines', {
        documentId: id,
        lines: edit.map((l) => ({ ...l, amount: Number(l.amount) })),
      });
      setCheck(r.check); setEdit(null); onChanged && onChanged();
    } catch (e) { setErr(e.message); }
    setBusy('');
  };
  const setLine = (i, patch) => setEdit((ls) => ls.map((l, n) => (n === i ? { ...l, ...patch, touched: true } : l)));

  if (!doc || !['pending', 'approved', 'paid'].includes(doc.status)) return null;
  if (check && check.status === 'entered') {
    // Entered by hand before this existed; nothing to show here.
    return null;
  }

  const lines = edit || (check && check.lines) || [];
  const sum = cents(lines.reduce((a, l) => a + (Number(l.amount) || 0), 0));
  const tied = lines.length > 0 && sum === approved;
  const pushed = check && (check.status === 'pushed' || check.qboBillId);

  return (
    <div style={card}>
      <div style={cardHead}>
        <span>QuickBooks lines{check && check.company ? <> · <CompanyTag company={check.company} /></> : ''}</span>
        {!edit && !pushed && check && (
          <span style={{ display: 'flex', gap: 6 }}>
            {check.linesSource !== 'trip' && (
              <button className="btn btn-ghost" disabled={!!busy} style={{ fontSize: 12 }}
                onClick={() => read(lines.length > 0)}>
                <RefreshCw size={12} /> {busy === 'read' ? 'Reading…' : (lines.length ? 'Read again' : 'Read the lines off the invoice')}
              </button>
            )}
            <button className="btn btn-ghost" disabled={!!busy} style={{ fontSize: 12 }} onClick={startEdit}>
              {lines.length ? 'Change lines or accounts' : 'Type the lines in'}
            </button>
          </span>
        )}
      </div>
      <div style={{ padding: '6px 16px 12px' }}>
        {!check && !err && <div style={{ fontSize: 13, color: 'var(--gray-500, #6b7280)', padding: '8px 0' }}>Checking…</div>}
        {pushed && (
          <div style={{ fontSize: 13, color: '#1c7c43', padding: '8px 0', display: 'flex', gap: 6, alignItems: 'center' }}>
            <CheckCircle2 size={14} /> In QuickBooks as bill #{doc.invoice_number}{check && check.qboBillId ? ' (QuickBooks id ' + check.qboBillId + ')' : ''}.
            {check && check.qboBillId && (
              <a href={qboBillUrl(check.qboBillId)} target="_blank" rel="noopener noreferrer"
                title={'Opens in QuickBooks Online, in ' + (check.company || 'the company you are signed in to')}>Open it in QuickBooks</a>
            )}
          </div>
        )}
        {check && check.linesSource === 'trip' && !edit && (
          <div style={{ fontSize: 12, color: 'var(--gray-500, #6b7280)', padding: '6px 0' }}>
            From the trip's keyed lines - the numbers the crew entered and the chain approved.
          </div>
        )}
        {lines.length > 0 && (
          <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={th}>Line</th><th style={th}>Account</th>
                <th style={{ ...th, textAlign: 'right' }}>Amount</th>{edit && <th style={th} />}
              </tr>
            </thead>
            <tbody>
              {lines.map((l, i) => (
                <tr key={i} style={{ borderTop: '1px solid var(--gray-100, #f3f4f6)' }}>
                  <td style={td}>
                    {edit
                      ? <input style={input} value={l.description || ''} onChange={(e) => setLine(i, { description: e.target.value })} />
                      : l.description}
                  </td>
                  <td style={{ ...td, minWidth: 260 }}>
                    {edit ? (
                      <select style={input} value={l.account_id || ''} onChange={(e) => setLine(i, { account_id: e.target.value, confidence: 'high', account_reason: 'chosen by a person' })}>
                        <option value="">Pick an account…</option>
                        {(accounts || []).map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                      </select>
                    ) : (
                      <span style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                        <span style={{ color: l.account_name ? 'inherit' : '#b42318' }}>{l.account_name || 'no account'}</span>
                        <ConfChip level={l.confidence} title={l.account_reason} />
                      </span>
                    )}
                    {!edit && l.account_reason && (
                      <div style={{ fontSize: 11, color: 'var(--gray-500, #6b7280)' }}>{l.account_reason}</div>
                    )}
                  </td>
                  <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap', width: 120 }}>
                    {edit
                      ? <input style={{ ...input, textAlign: 'right' }} inputMode="decimal" value={l.amount}
                          onChange={(e) => setLine(i, { amount: e.target.value })} />
                      : <>
                          <div>{money(l.amount)}</div>
                          {Number(l.quantity) > 0 && Number(l.unit_price) > 0 && !/@\s*\$/.test(String(l.description || '')) && (
                            <div style={{ fontSize: 11, color: 'var(--gray-500, #6b7280)', fontWeight: 400 }}>
                              {Number(l.quantity).toLocaleString('en-US')}{l.unit ? ' ' + l.unit : ''} @ {money(l.unit_price)}/{perLabel(l.unit)}
                            </div>
                          )}
                        </>}
                  </td>
                  {edit && (
                    <td style={{ ...td, width: 32 }}>
                      <button className="btn btn-ghost" style={{ padding: 4 }} title="Remove this line"
                        onClick={() => setEdit((ls) => ls.filter((_, n) => n !== i))}><Trash2 size={13} /></button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {(lines.length > 0 || edit) && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8, fontSize: 13, flexWrap: 'wrap' }}>
            {edit && (
              <button className="btn btn-ghost" style={{ fontSize: 12 }}
                onClick={() => setEdit((ls) => [...ls, { description: '', amount: '', account_id: '', touched: true }])}>
                <Plus size={12} /> Add a line
              </button>
            )}
            <span style={{ marginLeft: 'auto', color: tied ? '#1c7c43' : '#b42318', fontWeight: 600 }}>
              {tied ? <><Check size={12} /> Lines add up to the approved {money(approved)}</>
                : <>Lines add up to {money(sum)} · approved {money(approved)} · off by {money(cents(sum - approved))}</>}
            </span>
          </div>
        )}
        {edit && (
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 10 }}>
            <button className="btn btn-outline" disabled={busy === 'save'} onClick={() => setEdit(null)}>Cancel</button>
            <button className="btn btn-primary" disabled={busy === 'save'} onClick={save}>{busy === 'save' ? 'Saving…' : 'Save lines'}</button>
          </div>
        )}
        {check && !edit && !pushed && (check.issues || []).length > 0 && (
          <ul style={{ margin: '8px 0 0', paddingLeft: 18, fontSize: 12.5, color: '#9a4a1c' }}>
            {check.issues.map((i, n) => <li key={n}>{i.text}</li>)}
          </ul>
        )}
        {check && !edit && check.status === 'ready' && doc.status === 'approved' && (
          <div style={{ fontSize: 12.5, color: '#1c7c43', marginTop: 8 }}>Ready to send to QuickBooks from Ready to Pay.</div>
        )}
        {err && <div style={{ color: '#b42318', fontSize: 12.5, marginTop: 6 }}>{err}</div>}
      </div>
    </div>
  );
}
